import React, { useState } from "react";
import ProductSection from "../ProductsModule/Components/ProductSection";
import {
  Autocomplete,
  Checkbox,
  Radio,
  TextField,
  FormControlLabel,
  RadioGroup,
  CircularProgress,
  IconButton,
  Box,
  FormHelperText,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { X, Upload, Image as ImageIcon, Info } from "lucide-react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const options = [
  "VKC/3061",
  "VKC/3062",
  "VKC/3063",
  "VKC/3064",
  "VKC/3065",
  "VKC/3066",
  "VKC/3067",
  "VKC/3068",
  "VKC/3069",
  "VKC/3070",
  "VKC/3071",
];

const userOptions = ["User1", "User2", "User3", "User4", "User5"];

// Theme color
const PRIMARY_COLOR = "#5652B0";

const AddVoucher = () => {
  // Form state
  const [formValues, setFormValues] = useState({
    voucherName: "",
    discountPercentage: "",
    minOrderValue: "",
    maxOrderValue: "",
    description: "",
    voucherCode: "",
    usageLimit: "",
    usageLimitPerUser: "1",
  });

  // Error state
  const [errors, setErrors] = useState({});

  // Voucher type state
  const [voucherType, setVoucherType] = useState("common");

  // Date state
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(30, "day"));

  // Brand state
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandInput, setBrandInput] = useState("");
  const [allBrandsSelected, setAllBrandsSelected] = useState(true);

  // Article state
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [articleInput, setArticleInput] = useState("");
  const [allArticlesSelected, setAllArticlesSelected] = useState(true);

  // User state (for user-specific vouchers)
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userInput, setUserInput] = useState("");

  // Discount adjustment state
  const [discountAdjustment, setDiscountAdjustment] = useState("bill");

  // Poster state
  const [showPoster, setShowPoster] = useState(true);
  const [posterFile, setPosterFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);

  // Handle form input changes
  const handleChange = e => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
    setIsFormModified(true);
  };

  // Handle voucher type change
  const handleVoucherTypeChange = e => {
    setVoucherType(e.target.value);
    setIsFormModified(true);
  };

  // Handle discount adjustment change
  const handleDiscountAdjustmentChange = e => {
    setDiscountAdjustment(e.target.value);
    setIsFormModified(true);
  };

  // Handle brand selection
  const handleBrandChange = (event, value) => {
    setBrandInput(value);
  };

  const handleAddBrand = () => {
    if (brandInput && !selectedBrands.includes(brandInput)) {
      setSelectedBrands([...selectedBrands, brandInput]);
      setBrandInput("");
      setIsFormModified(true);
    }
  };

  const handleRemoveBrand = brand => {
    setSelectedBrands(selectedBrands.filter(item => item !== brand));
    setIsFormModified(true);
  };

  // Handle article selection
  const handleArticleChange = (event, value) => {
    setArticleInput(value);
  };

  const handleAddArticle = () => {
    if (articleInput && !selectedArticles.includes(articleInput)) {
      setSelectedArticles([...selectedArticles, articleInput]);
      setArticleInput("");
      setIsFormModified(true);
    }
  };

  const handleRemoveArticle = article => {
    setSelectedArticles(selectedArticles.filter(item => item !== article));
    setIsFormModified(true);
  };

  // Handle user selection (for user-specific vouchers)
  const handleUserChange = (event, value) => {
    setUserInput(value);
  };

  const handleAddUser = () => {
    if (userInput && !selectedUsers.includes(userInput)) {
      setSelectedUsers([...selectedUsers, userInput]);
      setUserInput("");
      setIsFormModified(true);
    }
  };

  const handleRemoveUser = user => {
    setSelectedUsers(selectedUsers.filter(item => item !== user));
    setIsFormModified(true);
  };

  // Handle poster file upload
  const handlePosterUpload = e => {
    const file = e.target.files[0];
    if (file) {
      setPosterFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setIsFormModified(true);
    }
  };

  // Remove poster image
  const handleRemovePoster = () => {
    setPosterFile(null);
    setPreviewUrl(null);
    setIsFormModified(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate form
    const newErrors = {};
    if (!formValues.voucherName) {
      newErrors.voucherName = "Voucher name is required";
    }
    if (!formValues.discountPercentage) {
      newErrors.discountPercentage = "Discount percentage is required";
    } else if (
      isNaN(formValues.discountPercentage) ||
      parseFloat(formValues.discountPercentage) <= 0 ||
      parseFloat(formValues.discountPercentage) > 100
    ) {
      newErrors.discountPercentage = "Discount must be between 0 and 100";
    }

    if (voucherType === "user" && selectedUsers.length === 0) {
      newErrors.users = "At least one user must be selected";
    }

    if (!allBrandsSelected && selectedBrands.length === 0) {
      newErrors.brands = "At least one brand must be selected";
    }

    if (formValues.usageLimit && isNaN(formValues.usageLimit)) {
      newErrors.usageLimit = "Usage limit must be a number";
    }

    if (formValues.usageLimitPerUser && isNaN(formValues.usageLimitPerUser)) {
      newErrors.usageLimitPerUser = "Usage limit per user must be a number";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);

      // Here you would add your API call to save the voucher
      // For now, we'll just simulate a delay
      setTimeout(() => {
        setIsSubmitting(false);
        // Reset form or navigate away
        setIsFormModified(false);
      }, 1500);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (isFormModified) {
      // Show confirmation dialog
      if (
        window.confirm(
          "Are you sure you want to cancel? All changes will be lost.",
        )
      ) {
        // Reset form or navigate away
      }
    } else {
      // Just navigate away
    }
  };

  return (
    <div className="ml-[2.083333vw] mr-[2.638888vw] bg-white mt-[4.8vh] h-[84vh] overflow-y-auto">
      <div className="bg-white rounded-xl pb-4 shadow-sm">
        <div className="flex items-center justify-between px-6 h-[8.7vh] border-b">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Add Voucher
          </h1>
        </div>

        {/* Voucher Type Selection */}
        <div className="px-6 mt-4">
          <RadioGroup
            row
            name="voucherType"
            value={voucherType}
            onChange={handleVoucherTypeChange}>
            <FormControlLabel
              value="common"
              control={<Radio color="secondary" />}
              label={
                <span className="font-outfit font-medium">Common Voucher</span>
              }
            />
            <FormControlLabel
              value="user"
              control={<Radio color="secondary" />}
              label={
                <span className="font-outfit font-medium">
                  User-Specific Voucher
                </span>
              }
            />
          </RadioGroup>
        </div>

        {/* Basic Information */}
        <div className="grid grid-cols-3 gap-6 w-[90%] mt-4 px-6">
          <TextField
            id="voucher-name"
            label="Voucher Name"
            name="voucherName"
            variant="outlined"
            className={
              errors.voucherName
                ? ""
                : "border-dotted border-2 border-green-400"
            }
            required={true}
            size="small"
            value={formValues.voucherName}
            onChange={handleChange}
            error={errors?.voucherName ? true : false}
            helperText={errors?.voucherName}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: PRIMARY_COLOR,
                },
              },
            }}
          />
          <TextField
            id="voucher-code"
            label="Voucher Code"
            name="voucherCode"
            variant="outlined"
            size="small"
            value={formValues.voucherCode}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Leave empty to auto-generate code">
                    <Info size={16} className="text-gray-400" />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: PRIMARY_COLOR,
                },
              },
            }}
          />
          <TextField
            id="discount-percentage"
            label="Discount %"
            name="discountPercentage"
            variant="outlined"
            required={true}
            size="small"
            value={formValues.discountPercentage}
            onChange={handleChange}
            error={errors?.discountPercentage ? true : false}
            helperText={errors?.discountPercentage}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: PRIMARY_COLOR,
                },
              },
            }}
          />
        </div>

        <div className="grid grid-cols-3 gap-6 w-[90%] mt-4 px-6">
          <TextField
            id="min-order-value"
            label="Minimum Order Value (Optional)"
            name="minOrderValue"
            variant="outlined"
            size="small"
            value={formValues.minOrderValue}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: PRIMARY_COLOR,
                },
              },
            }}
          />
          <TextField
            id="max-order-value"
            label="Maximum Order Value (Optional)"
            name="maxOrderValue"
            variant="outlined"
            size="small"
            value={formValues.maxOrderValue}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: PRIMARY_COLOR,
                },
              },
            }}
          />
          <TextField
            id="usage-limit"
            label="Total Usage Limit"
            name="usageLimit"
            variant="outlined"
            size="small"
            value={formValues.usageLimit}
            onChange={handleChange}
            error={errors?.usageLimit ? true : false}
            helperText={errors?.usageLimit || "Leave empty for unlimited usage"}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: PRIMARY_COLOR,
                },
              },
            }}
          />
        </div>

        <div className="grid grid-cols-3 gap-6 w-[90%] mt-4 px-6">
          <TextField
            id="usage-limit-per-user"
            label="Usage Limit Per User"
            name="usageLimitPerUser"
            variant="outlined"
            size="small"
            value={formValues.usageLimitPerUser}
            onChange={handleChange}
            error={errors?.usageLimitPerUser ? true : false}
            helperText={errors?.usageLimitPerUser}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: PRIMARY_COLOR,
                },
              },
            }}
          />
        </div>

        <div className="w-[90%] mt-4 px-6">
          <TextField
            id="description"
            label="Description (Optional)"
            name="description"
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            placeholder="Add any additional information about the voucher..."
            value={formValues.description}
            onChange={handleChange}
            inputProps={{
              maxLength: 500,
              style: { resize: "none" },
            }}
            helperText={`${formValues.description.length}/500 characters`}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: PRIMARY_COLOR,
                },
              },
            }}
          />
        </div>

        {/* User Section (only for user-specific vouchers) */}
        {voucherType === "user" && (
          <>
            <ProductSection data={"User"} />
            <div className="w-[90%] mt-4 px-6">
              <div className="flex flex-wrap items-center gap-3">
                <Autocomplete
                  id="user-select"
                  freeSolo
                  value={userInput}
                  size="small"
                  options={userOptions}
                  sx={{
                    width: 220,
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: PRIMARY_COLOR,
                      },
                    },
                  }}
                  onChange={handleUserChange}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Enter User"
                      variant="outlined"
                      error={errors?.users ? true : false}
                      helperText={errors?.users}
                    />
                  )}
                />
                <button
                  onClick={handleAddUser}
                  className="px-3 py-1 bg-[#5652B0] text-white rounded-md text-sm hover:bg-[#4A47A3] transition-colors">
                  Add
                </button>

                {selectedUsers.map((user, index) => (
                  <div
                    key={index}
                    className="flex items-center border border-gray-300 hover:border-[#5652B0] rounded-full px-3 py-1 transition-colors">
                    <span className="font-outfit mr-2">{user}</span>
                    <button
                      onClick={() => handleRemoveUser(user)}
                      className="p-1 rounded-full bg-gray-200 hover:bg-gray-300">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Discount Adjustment */}
        <ProductSection data={"Discount Adjustment"} />
        <div className="w-[90%] mt-4 px-6">
          <RadioGroup
            row
            name="discountAdjustment"
            value={discountAdjustment}
            onChange={handleDiscountAdjustmentChange}>
            <FormControlLabel
              value="bill"
              control={<Radio color="secondary" />}
              label={<span className="font-outfit font-medium">On Bill</span>}
            />
            <FormControlLabel
              value="wallet"
              control={<Radio color="secondary" />}
              label={<span className="font-outfit font-medium">On Wallet</span>}
            />
          </RadioGroup>
        </div>

        {/* Validity */}
        <ProductSection data={"Validity"} />
        <div className="flex flex-row gap-6 w-[90%] mt-4 px-6">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <DatePicker
                label="From"
                name="startDate"
                value={startDate}
                onChange={newValue => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: PRIMARY_COLOR,
                        },
                      },
                    },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <DatePicker
                label="To"
                name="endDate"
                value={endDate}
                onChange={newValue => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: PRIMARY_COLOR,
                        },
                      },
                    },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
        </div>

        {/* Brand */}
        <ProductSection data={"Brand"} />
        <div className="w-[90%] mt-4 px-6">
          <div className="flex items-center mb-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allBrandsSelected}
                  onChange={e => {
                    setAllBrandsSelected(e.target.checked);
                    if (e.target.checked) {
                      setSelectedBrands([]);
                    }
                    setIsFormModified(true);
                  }}
                  color="default"
                  sx={{
                    "&.Mui-checked": {
                      color: PRIMARY_COLOR,
                    },
                  }}
                />
              }
              label={
                <span className="font-outfit font-medium">
                  Add voucher for all brands
                </span>
              }
            />
          </div>

          {!allBrandsSelected && (
            <div className="flex flex-wrap items-center gap-3">
              <Autocomplete
                id="brand-select"
                freeSolo
                value={brandInput}
                size="small"
                options={options}
                sx={{
                  width: 220,
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: PRIMARY_COLOR,
                    },
                  },
                }}
                onChange={handleBrandChange}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Brand"
                    variant="outlined"
                    error={errors?.brands ? true : false}
                    helperText={errors?.brands}
                  />
                )}
              />
              <button
                onClick={handleAddBrand}
                className="px-3 py-1 bg-[#5652B0] text-white rounded-md text-sm hover:bg-[#4A47A3] transition-colors">
                Add
              </button>

              {selectedBrands.map((brand, index) => (
                <div
                  key={index}
                  className="flex items-center border border-gray-300 hover:border-[#5652B0] rounded-full px-3 py-1 transition-colors">
                  <span className="font-outfit mr-2">{brand}</span>
                  <button
                    onClick={() => handleRemoveBrand(brand)}
                    className="p-1 rounded-full bg-gray-200 hover:bg-gray-300">
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Articles */}
        <div className="w-[90%] mt-4 px-6">
          <div className="flex items-center mb-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allArticlesSelected}
                  onChange={e => {
                    setAllArticlesSelected(e.target.checked);
                    if (e.target.checked) {
                      setSelectedArticles([]);
                    }
                    setIsFormModified(true);
                  }}
                  color="default"
                  sx={{
                    "&.Mui-checked": {
                      color: PRIMARY_COLOR,
                    },
                  }}
                />
              }
              label={
                <span className="font-outfit font-medium">
                  Apply for all articles for selected brands
                </span>
              }
            />
          </div>

          {!allArticlesSelected && (
            <div className="flex flex-wrap items-center gap-3">
              <Autocomplete
                id="article-select"
                freeSolo
                value={articleInput}
                size="small"
                options={options}
                sx={{
                  width: 220,
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: PRIMARY_COLOR,
                    },
                  },
                }}
                onChange={handleArticleChange}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Article ID"
                    variant="outlined"
                  />
                )}
              />
              <button
                onClick={handleAddArticle}
                className="px-3 py-1 bg-[#5652B0] text-white rounded-md text-sm hover:bg-[#4A47A3] transition-colors">
                Add
              </button>

              {selectedArticles.map((article, index) => (
                <div
                  key={index}
                  className="flex items-center border border-gray-300 hover:border-[#5652B0] rounded-full px-3 py-1 transition-colors">
                  <span className="font-outfit mr-2">{article}</span>
                  <button
                    onClick={() => handleRemoveArticle(article)}
                    className="p-1 rounded-full bg-gray-200 hover:bg-gray-300">
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Poster */}
        <ProductSection data={"Poster"} />
        <div className="w-[90%] mt-4 px-6">
          <div className="flex items-center mb-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showPoster}
                  onChange={e => {
                    setShowPoster(e.target.checked);
                    setIsFormModified(true);
                  }}
                  color="default"
                  sx={{
                    "&.Mui-checked": {
                      color: PRIMARY_COLOR,
                    },
                  }}
                />
              }
              label={
                <span className="font-outfit font-medium">
                  Show Poster for Voucher
                </span>
              }
            />
          </div>

          {showPoster && (
            <div className="flex flex-col">
              <div className="mb-2">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Voucher Image
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Upload an image to display with this voucher. Recommended
                  ratio 2:1 (width:height)
                </p>
              </div>

              {previewUrl ? (
                <div className="relative w-fit">
                  <div className="border border-gray-200 hover:border-[#5652B0] rounded-lg overflow-hidden w-[400px] h-[200px] flex items-center justify-center transition-colors">
                    <img
                      src={previewUrl}
                      alt="Voucher preview"
                      className="object-contain max-w-full max-h-full"
                    />
                  </div>
                  <button
                    onClick={handleRemovePoster}
                    className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100">
                    <X size={16} className="text-gray-700" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 hover:border-[#5652B0] rounded-lg p-6 w-[400px] h-[200px] flex items-center justify-center transition-colors">
                  <input
                    type="file"
                    id="poster-upload"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePosterUpload}
                  />
                  <label
                    htmlFor="poster-upload"
                    className="flex flex-col items-center justify-center cursor-pointer">
                    <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mb-2">
                      <ImageIcon size={24} className="text-[#5652B0]" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Click to upload image
                    </p>
                    <p className="text-xs text-gray-500">
                      SVG, PNG, JPG or GIF (max. 2MB)
                    </p>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-x-4 px-6 py-8 border-t mt-8">
          <button
            onClick={handleCancel}
            disabled={isSubmitting}
            className={`px-6 py-2.5 rounded-lg text-base font-medium transition-all ${
              isFormModified
                ? "border-2 border-[#5652B0] text-[#5652B0] hover:bg-[#5652B0] hover:text-white"
                : "border-2 border-gray-200 text-gray-400 cursor-not-allowed"
            } ${isSubmitting && "opacity-50 cursor-not-allowed"}`}>
            Cancel
          </button>

          <button
            className={`px-6 py-2.5 rounded-lg text-base font-medium text-white transition-all ${
              isSubmitting ? "bg-purple-500" : "bg-[#5652B0] hover:bg-[#4A47A3]"
            }`}
            onClick={handleSubmit}
            disabled={isSubmitting}>
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddVoucher;
